import supabase from '@/supabase';
import type { DirectMessage } from '@/types/chat';

const MESSAGE_ATTACHMENTS_BUCKET = 'message-attachments';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const MAX_FILE_SIZE = 52428800; // 50MB

export interface UploadAttachmentResult {
  success: boolean;
  url?: string;
  type?: DirectMessage['attachment_type'];
  error?: string;
}

/**
 * Determines the attachment type based on the file's MIME type
 */
function getAttachmentType(mimeType: string): DirectMessage['attachment_type'] {
  if (ALLOWED_IMAGE_TYPES.includes(mimeType)) {
    return 'image';
  }
  if (ALLOWED_VIDEO_TYPES.includes(mimeType)) {
    return 'video';
  }
  return null;
}

/**
 * Validates a file before upload
 */
function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    return `File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`;
  }

  const allAllowedTypes = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES];
  if (!allAllowedTypes.includes(file.type)) {
    return 'File type not supported. Allowed: JPEG, PNG, GIF, WebP, MP4, MOV';
  }

  return null;
}

/**
 * Uploads an attachment file to the message-attachments bucket
 * Files are stored in a folder named after the user's ID for RLS enforcement
 */
export async function uploadAttachment(
  file: File,
  userId: string
): Promise<UploadAttachmentResult> {
  // Validate file
  const validationError = validateFile(file);
  if (validationError) {
    return { success: false, error: validationError };
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop() || 'bin';
  const fileName = `${userId}/${Date.now()}-${crypto.randomUUID()}.${fileExt}`;

  try {
    const { error: uploadError } = await supabase.storage
      .from(MESSAGE_ATTACHMENTS_BUCKET)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('[uploadAttachment] Upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    // Get signed URL for private bucket access
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from(MESSAGE_ATTACHMENTS_BUCKET)
      .createSignedUrl(fileName, 60 * 60 * 24 * 7); // 7-day expiry

    if (signedUrlError) {
      console.error('[uploadAttachment] Signed URL error:', signedUrlError);
      return { success: false, error: 'Failed to generate access URL' };
    }

    const attachmentType = getAttachmentType(file.type);

    return {
      success: true,
      url: signedUrlData.signedUrl,
      type: attachmentType,
    };
  } catch (err) {
    console.error('[uploadAttachment] Unexpected error:', err);
    return { success: false, error: 'Failed to upload attachment' };
  }
}

/**
 * Uploads multiple attachment files
 * Returns results for all files (some may succeed, some may fail)
 */
export async function uploadAttachments(
  files: File[],
  userId: string
): Promise<UploadAttachmentResult[]> {
  const results = await Promise.all(
    files.map((file) => uploadAttachment(file, userId))
  );
  return results;
}
